import React from 'react';
import { twMerge } from 'tailwind-merge';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import WebUiPageWrapper from '../../components/page-wrapper';
import {
  priorityLabel,
  poeStatusLabel,
  type PoeCfgRow,
  type PoeStateRow,
  formatPoeGlobalBudget,
  normalizePoePortArray,
  formatPoeLimitResponse,
} from './status-poe-utils';

const poeClassW = [15.4, 4, 7, 15.4, 30];
const userLimit = [30, 2, 4, 6, 8, 10, 12, 15, 20, 24, 18];

type PoeTableRow = {
  port: number;
  name: string;
  mode: string;
  prio: string;
  status: string;
  classTxt: string;
  limitClass: string;
  limitPwr: string;
  allocated: string;
  power: string;
  volt: string;
  curr: string;
  down: boolean;
};

function buildRows(cfg: PoeCfgRow[], state: PoeStateRow[]): PoeTableRow[] {
  const count = Math.max(cfg.length, state.length);
  return Array.from({ length: count }, (_, i) => {
    const c = cfg[i] ?? {};
    const s = state[i] ?? {};
    const cls = s.poeclass ?? -1;
    const classTxt =
      cls === -1 ? '-' : `${cls} (${(poeClassW[cls] ?? 0).toFixed(1)}W)`;
    const limIdx = c.limit_pwr ?? 0;
    const limCap = userLimit[limIdx];
    const alloc =
      cls === -1 ? '-' : `${Math.min(poeClassW[cls] ?? 0, limCap ?? 0)}W`;
    return {
      port: i + 1,
      name: c.name ?? '-',
      mode: c.enable ? 'enabled' : 'disabled',
      prio: priorityLabel(c.prio),
      status: poeStatusLabel(s.status),
      classTxt,
      limitClass: `${poeClassW[c.limit ?? 0] ?? 0}W (Class ${c.limit ?? 0})`,
      limitPwr: limCap != null ? `${limCap}W` : '-',
      allocated: alloc,
      power:
        s.curr && s.volt
          ? String(Math.round((s.curr / 10) * (s.volt / 1000)) / 100)
          : '-',
      volt: s.volt ? String(Math.round(s.volt / 100) / 10) : '-',
      curr: s.curr ? String(Math.round(s.curr * 10) / 10) : '-',
      down: cls === -1,
    };
  });
}

export default function WebUiStatusPoePage() {
  const [maxPower, setMaxPower] = React.useState('-');
  const [allocated, setAllocated] = React.useState('-');
  const [rows, setRows] = React.useState<PoeTableRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [limitData, cfgRaw, global, stateRaw] = await Promise.all([
      postWebUiApi<unknown>({
        service: 'poe',
        access: 'get',
        command: 'get limit',
      }),
      postWebUiApi<unknown>({
        service: 'poe',
        access: 'get',
        command: 'get port cfg state',
      }),
      postWebUiApi<unknown>({
        service: 'poe',
        access: 'get',
        command: 'get global state',
      }),
      postWebUiApi<unknown>({
        service: 'poe',
        access: 'get',
        command: 'get poe state',
      }),
    ]);

    setMaxPower(formatPoeLimitResponse(limitData));
    setAllocated(formatPoeGlobalBudget(global));

    const cfg = normalizePoePortArray<PoeCfgRow>(cfgRaw);
    const state = normalizePoePortArray<PoeStateRow>(stateRaw);
    setRows(buildRows(cfg, state));
  }, []);

  React.useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        await reload();
        if (alive) setError(null);
      } catch (err) {
        if (alive) setError((err as Error).message);
      }
    };
    load().catch(() => {});
    const t = window.setInterval(() => load().catch(() => {}), 4000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [reload]);

  return (
    <WebUiPageWrapper titleList={['Status', 'PoE']}>
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="webui-panel w-[calc(25%-20px)]">
        <h4 className="font-bold">Global Usage</h4>
        <table className="webui-table w-full">
          <thead>
            <tr className="webui-table-row-head">
              <th>Parameter</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="webui-table-row-content">
              <td>Power Budget</td>
              <td>{maxPower}</td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Power allocated</td>
              <td>{allocated}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2">
        <table className="webui-table w-full">
          <thead>
            <tr className="webui-table-row-head">
              <th>Port</th>
              <th>PD Name</th>
              <th>PoE Mode</th>
              <th>Priority</th>
              <th>PoE Status</th>
              <th>PD Class</th>
              <th>Limit Class</th>
              <th>Power Limit</th>
              <th>Power allocated</th>
              <th>Power Usage (W)</th>
              <th>Voltage (V)</th>
              <th>Current (mA)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.port}
                className={twMerge(
                  'webui-table-row-content',
                  r.down ? 'text-red-500' : '',
                )}
              >
                <td>{r.port}</td>
                <td className="truncate">{r.name}</td>
                <td>{r.mode}</td>
                <td className="text-center">{r.prio}</td>
                <td className="text-center">{r.status}</td>
                <td className="text-center">{r.classTxt}</td>
                <td className="text-center">{r.limitClass}</td>
                <td className="text-center">{r.limitPwr}</td>
                <td className="text-center">{r.allocated}</td>
                <td className="text-center">{r.power}</td>
                <td className="text-center">{r.volt}</td>
                <td className="text-center">{r.curr}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h4 className="mt-4 font-bold">PoE Status</h4>
      <table className="text-sm">
        <tbody>
          <tr>
            <td>disabled</td>
            <td className="text-center">=</td>
            <td>PoE port is disabled/off</td>
          </tr>
          <tr>
            <td>not detected</td>
            <td className="text-center">=</td>
            <td>PD is not connected or not valid</td>
          </tr>
          <tr>
            <td>powered</td>
            <td className="text-center">=</td>
            <td>PD is supplied by PoE port</td>
          </tr>
          <tr>
            <td>blocked</td>
            <td className="text-center">=</td>
            <td>
              Power supply of PD is blocked due to exceeded total power budget
            </td>
          </tr>
          <tr>
            <td>dropped</td>
            <td className="text-center">=</td>
            <td>
              Power supply of PD is turned off due to exceeded PoE class limit
            </td>
          </tr>
          <tr>
            <td>error</td>
            <td className="text-center">=</td>
            <td>internal PoE error</td>
          </tr>
        </tbody>
      </table>
    </WebUiPageWrapper>
  );
}
